import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "@/components/Toaster";
import {
  Plus, ListTodo, Loader2, AlertCircle, CheckCircle2,
  Calendar, FolderLock, ArrowRight, Trash2, ShieldAlert,
  X, MessageSquare, Send, User as UserIcon,
} from "lucide-react";
import TaskFormModal from "@/components/TaskFormModal";

/**
 * Tasks — boardroom-grade todo list scoped to the VDR workspace.
 *
 * Layout
 *   • Header strip with room filter + "New task" CTA.
 *   • Four-column Kanban (To do / In progress / Blocked / Done) for an
 *     instant glance at workload distribution.
 *   • Task cards open a side panel (TaskDetail) with full description,
 *     comments, and inline status / priority / assignee controls.
 *
 * Access gating
 *   • The sidebar entry is already hidden for users without the flag;
 *     this page additionally renders an explicit "feature disabled"
 *     screen if a user reaches it directly via the URL.
 */

/**
 * Two-letter initials from a user's name (or email if name is missing).
 *
 *   "Sean Smith"          → "SS"
 *   "sean.smith@..."      → "SE"   (local-part, first two letters)
 *   "Mary-Anne O'Brien"   → "MO"   (first name's first letter + surname's first letter)
 *   "Cher"                → "CH"   (single-name fallback)
 */
const initials = (u) => {
  if (!u) return "??";
  const name = (u.name || "").trim();
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].slice(0, 2).toUpperCase();
  }
  const email = (u.email || "").trim();
  if (email) return email.split("@")[0].slice(0, 2).toUpperCase();
  return "??";
};

/**
 * Deterministic avatar tone — maps a user's stable id (or email) onto one
 * of eight brand-safe colour pairs. We hash the source string with a
 * simple FNV-1a so the same user lands on the same colour everywhere
 * (Kanban cards, detail panel, assignee picker) without needing to store
 * a colour per user.
 *
 * Tailwind utility pairs `bg-<x>-500/15 text-<x>-500` give us subtle
 * tinted chips that read cleanly on both light and dark themes.
 */
const AVATAR_TONES = [
  "bg-emerald-500/15 text-emerald-500",
  "bg-sky-500/15 text-sky-500",
  "bg-amber-500/20 text-amber-600 dark:text-amber-400",
  "bg-rose-500/15 text-rose-500",
  "bg-violet-500/15 text-violet-500",
  "bg-teal-500/15 text-teal-500",
  "bg-fuchsia-500/15 text-fuchsia-500",
  "bg-indigo-500/15 text-indigo-500",
];
const avatarTone = (u) => {
  const key = String((u?.id || u?.email || u?.name || "") + "");
  if (!key) return AVATAR_TONES[0];
  let h = 0x811c9dc5; // FNV offset basis
  for (let i = 0; i < key.length; i += 1) {
    h ^= key.charCodeAt(i);
    h = (h * 0x01000193) >>> 0; // FNV prime, keep as uint32
  }
  return AVATAR_TONES[h % AVATAR_TONES.length];
};

const COLUMNS = [
  { key: "todo",         label: "To do",         tone: "text-muted-foreground" },
  { key: "in_progress",  label: "In progress",   tone: "text-amber-500" },
  { key: "blocked",      label: "Blocked",       tone: "text-rose-500" },
  { key: "done",         label: "Done",          tone: "text-emerald-500" },
];

const PRIORITY_META = {
  low:    { label: "Low",    pill: "bg-secondary text-muted-foreground" },
  medium: { label: "Medium", pill: "bg-sky-500/15 text-sky-500" },
  high:   { label: "High",   pill: "bg-amber-500/15 text-amber-500" },
  urgent: { label: "Urgent", pill: "bg-rose-500/15 text-rose-500" },
};

export default function TasksPage() {
  const { user: me } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [access, setAccess] = useState(null);  // { has_access, is_privileged }
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [docs, setDocs] = useState({}); // roomId -> [docs]
  const [loading, setLoading] = useState(true);
  const [filterRoom, setFilterRoom] = useState(searchParams.get("room") || "all");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null); // task being edited
  const [selected, setSelected] = useState(null); // task open in detail panel

  const refresh = async () => {
    try {
      const params = filterRoom && filterRoom !== "all" ? { room_id: filterRoom } : {};
      const { data } = await api.get("/tasks", { params });
      setTasks(data);
      // Open task by query-param (used by notification deep-links)
      const id = searchParams.get("id");
      if (id) {
        const found = data.find((t) => t.id === id);
        if (found) setSelected(found);
      }
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  // Boot: verify access, hydrate users + rooms + tasks in parallel.
  useEffect(() => {
    (async () => {
      try {
        const { data: a } = await api.get("/tasks/access/me");
        setAccess(a);
        if (!a.has_access) { setLoading(false); return; }
        const [u, r] = await Promise.all([
          api.get("/users").catch(() => ({ data: [] })),
          api.get("/rooms"),
        ]);
        setUsers(u.data || []);
        setRooms(r.data || []);
        await refresh();
      } catch (e) {
        toast.error(formatError(e));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-load tasks when the room filter changes.
  useEffect(() => {
    if (!access?.has_access) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterRoom]);

  // Fan-out: fetch documents for any room used inside a task so we can
  // resolve the document name in cards / detail panel.
  useEffect(() => {
    const need = new Set(
      tasks.filter((t) => t.room_id && !docs[t.room_id]).map((t) => t.room_id)
    );
    if (need.size === 0) return;
    (async () => {
      const next = { ...docs };
      await Promise.all([...need].map(async (rid) => {
        try {
          const { data } = await api.get(`/rooms/${rid}/documents`);
          next[rid] = data;
        } catch {
          next[rid] = [];
        }
      }));
      setDocs(next);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const userById = useMemo(() => {
    const m = new Map();
    users.forEach((u) => m.set(u.id, u));
    return m;
  }, [users]);

  const roomById = useMemo(() => {
    const m = new Map();
    rooms.forEach((r) => m.set(r.id, r));
    return m;
  }, [rooms]);

  const docName = (roomId, docId) => {
    const list = docs[roomId] || [];
    return list.find((d) => d.id === docId)?.name || null;
  };

  const grouped = useMemo(() => {
    const buckets = { todo: [], in_progress: [], blocked: [], done: [] };
    tasks.forEach((t) => {
      const k = COLUMNS.find((c) => c.key === t.status) ? t.status : "todo";
      buckets[k].push(t);
    });
    return buckets;
  }, [tasks]);

  const onCreated = async () => { setShowCreate(false); await refresh(); };
  const onUpdated = async (updated) => {
    if (updated) {
      setTasks((arr) => arr.map((t) => (t.id === updated.id ? updated : t)));
      if (selected?.id === updated.id) setSelected(updated);
    } else {
      await refresh();
    }
    setEditing(null);
  };

  const updateStatus = async (task, status) => {
    try {
      const { data } = await api.patch(`/tasks/${task.id}`, { status });
      setTasks((arr) => arr.map((t) => (t.id === task.id ? data : t)));
      if (selected?.id === task.id) setSelected(data);
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const remove = async (task) => {
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    try {
      await api.delete(`/tasks/${task.id}`);
      setTasks((arr) => arr.filter((t) => t.id !== task.id));
      if (selected?.id === task.id) setSelected(null);
      toast.success("Task deleted");
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center gap-2 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading tasks…
      </div>
    );
  }

  if (!access?.has_access) {
    return (
      <div className="p-12 max-w-xl mx-auto text-center">
        <ShieldAlert className="w-10 h-10 text-muted-foreground mx-auto" />
        <h2 className="mt-4 font-display text-2xl tracking-tight">Tasks aren&apos;t enabled for your account</h2>
        <p className="mt-3 text-sm text-muted-foreground">
          Ask an Owner to grant you access from the Users page. Owners can
          flip task access for any user without changing their role.
        </p>
      </div>
    );
  }

  return (
    <div className="p-8 max-w-[1600px] mx-auto" data-testid="tasks-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="label-mono text-muted-foreground">— Workspace / Tasks</div>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl tracking-tight">Tasks</h1>
          <p className="mt-2 text-sm text-muted-foreground max-w-xl">
            Track diligence checklists, follow-ups, and action items. Link a task to a data room,
            folder, or document for instant context.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="tasks-room-filter"
            value={filterRoom}
            onChange={(e) => setFilterRoom(e.target.value)}
            className="px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            <option value="all">All rooms</option>
            {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <button
            data-testid="tasks-new-btn"
            onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition"
          >
            <Plus className="w-4 h-4" /> New task
          </button>
        </div>
      </div>

      {/* Kanban */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {COLUMNS.map((c) => (
          <div
            key={c.key}
            data-testid={`tasks-col-${c.key}`}
            className="bg-card border border-border rounded-md flex flex-col min-h-[300px]"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-border">
              <div className="flex items-center gap-2">
                <ListTodo className={`w-3.5 h-3.5 ${c.tone}`} />
                <span className="label-mono text-foreground">{c.label}</span>
              </div>
              <span className="label-mono text-muted-foreground" data-testid={`tasks-col-count-${c.key}`}>
                {grouped[c.key].length}
              </span>
            </div>
            <div className="p-3 space-y-2 flex-1">
              {grouped[c.key].length === 0 ? (
                <div className="text-xs text-muted-foreground text-center py-6">
                  No tasks
                </div>
              ) : grouped[c.key].map((t) => (
                <TaskCard
                  key={t.id}
                  task={t}
                  room={t.room_id ? roomById.get(t.room_id) : null}
                  docName={t.document_id ? docName(t.room_id, t.document_id) : null}
                  userById={userById}
                  onOpen={() => setSelected(t)}
                  onChangeStatus={(s) => updateStatus(t, s)}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Create / Edit modal */}
      {showCreate && (
        <TaskFormModal
          mode="create"
          users={users}
          rooms={rooms}
          onClose={() => setShowCreate(false)}
          onSaved={onCreated}
        />
      )}
      {editing && (
        <TaskFormModal
          mode="edit"
          task={editing}
          users={users}
          rooms={rooms}
          onClose={() => setEditing(null)}
          onSaved={onUpdated}
        />
      )}

      {/* Detail side panel */}
      {selected && (
        <TaskDetailPanel
          task={selected}
          users={users}
          userById={userById}
          roomName={selected.room_id ? roomById.get(selected.room_id)?.name : null}
          docName={selected.document_id ? docName(selected.room_id, selected.document_id) : null}
          canEdit={
            access.is_privileged
            || selected.created_by === me?.id
            || (selected.assignees || []).includes(me?.id)
          }
          canDelete={access.is_privileged || selected.created_by === me?.id}
          onClose={() => { setSelected(null); setSearchParams({}); }}
          onEdit={() => { setEditing(selected); }}
          onChangeStatus={(s) => updateStatus(selected, s)}
          onDelete={() => remove(selected)}
          onCommented={(updatedTask) => {
            setTasks((arr) => arr.map((t) => (t.id === updatedTask.id ? updatedTask : t)));
            setSelected(updatedTask);
          }}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------
// Task card — the unit visible inside each Kanban column.
// Kept dense so a 4-column screen on a 1280-wide laptop stays scannable.
// --------------------------------------------------------------------------
function TaskCard({ task, room, docName, userById, onOpen, onChangeStatus }) {
  const pri = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const overdue = task.due_date && task.status !== "done" && new Date(task.due_date) < new Date();
  const assignees = (task.assignees || []).map((id) => userById.get(id)).filter(Boolean);
  return (
    <button
      type="button"
      data-testid="task-card"
      onClick={onOpen}
      className="w-full text-left bg-background border border-border rounded-md p-3 hover:border-primary/60 hover:bg-secondary/40 transition"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="text-sm font-medium text-foreground line-clamp-2">{task.title}</div>
        <span className={`label-mono px-1.5 py-0.5 rounded-full ${pri.pill} flex-shrink-0`}>
          {pri.label}
        </span>
      </div>
      {task.description && (
        <p className="text-xs text-muted-foreground mt-1.5 line-clamp-2">
          {task.description}
        </p>
      )}
      <div className="mt-3 flex items-center gap-3 flex-wrap text-xs text-muted-foreground">
        {room && (
          <span className="inline-flex items-center gap-1">
            <FolderLock className="w-3 h-3" />
            <span className="truncate max-w-[120px]">{room.name}</span>
          </span>
        )}
        {docName && (
          <span className="inline-flex items-center gap-1 truncate">
            <ArrowRight className="w-3 h-3" />
            <span className="truncate max-w-[120px]">{docName}</span>
          </span>
        )}
        {task.due_date && (
          <span className={`inline-flex items-center gap-1 ${overdue ? "text-rose-500" : ""}`}>
            <Calendar className="w-3 h-3" /> {task.due_date}
          </span>
        )}
      </div>
      {assignees.length > 0 && (
        <div className="mt-2 flex items-center gap-1 flex-wrap">
          {assignees.slice(0, 3).map((u) => (
            <span
              key={u.id}
              title={u.name ? `${u.name} · ${u.email}` : u.email}
              className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-semibold tracking-wide ${avatarTone(u)}`}
            >
              {initials(u)}
            </span>
          ))}
          {assignees.length > 3 && (
            <span className="label-mono text-muted-foreground">+{assignees.length - 3}</span>
          )}
        </div>
      )}
      {/* Quick status shortcut */}
      <div className="mt-3 flex items-center gap-1.5 flex-wrap">
        {COLUMNS.filter((c) => c.key !== task.status).map((c) => (
          <span
            key={c.key}
            role="button"
            data-testid={`task-quickmove-${c.key}`}
            onClick={(e) => { e.stopPropagation(); onChangeStatus(c.key); }}
            className="label-mono text-muted-foreground hover:text-foreground border border-border rounded-full px-2 py-0.5 cursor-pointer transition"
          >
            → {c.label}
          </span>
        ))}
      </div>
    </button>
  );
}

// --------------------------------------------------------------------------
// Task detail panel — slides in from the right with description, links,
// status / priority / assignee controls, and the comments thread.
// --------------------------------------------------------------------------
function TaskDetailPanel({
  task, users, userById, roomName, docName,
  canEdit, canDelete,
  onClose, onEdit, onChangeStatus, onDelete, onCommented,
}) {
  const pri = PRIORITY_META[task.priority] || PRIORITY_META.medium;
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const submitComment = async (e) => {
    e.preventDefault();
    if (!comment.trim()) return;
    setBusy(true);
    try {
      await api.post(`/tasks/${task.id}/comments`, { body: comment.trim() });
      const { data } = await api.get(`/tasks/${task.id}`);
      onCommented(data);
      setComment("");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[55]" data-testid="task-detail-panel">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <aside className="absolute right-0 top-0 h-screen w-full max-w-md bg-card border-l border-border flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="label-mono text-muted-foreground">— Task</div>
            <h3 className="mt-1 font-display text-xl tracking-tight">{task.title}</h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition" data-testid="task-detail-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="flex flex-wrap items-center gap-2">
            <span className={`label-mono px-2 py-1 rounded-full ${pri.pill}`}>
              {pri.label}
            </span>
            {task.due_date && (
              <span className="label-mono text-muted-foreground inline-flex items-center gap-1">
                <Calendar className="w-3 h-3" /> {task.due_date}
              </span>
            )}
          </div>

          {/* Status quick-switcher */}
          <div>
            <div className="label-mono text-muted-foreground mb-2">Status</div>
            <div className="flex flex-wrap gap-2">
              {COLUMNS.map((c) => (
                <button
                  key={c.key}
                  type="button"
                  disabled={!canEdit}
                  onClick={() => onChangeStatus(c.key)}
                  data-testid={`task-detail-status-${c.key}`}
                  className={`px-3 py-1.5 rounded-md text-xs border transition disabled:opacity-50 ${
                    task.status === c.key
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border hover:bg-secondary text-muted-foreground"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          {task.description && (
            <div>
              <div className="label-mono text-muted-foreground mb-2">Description</div>
              <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">{task.description}</p>
            </div>
          )}

          {(roomName || docName) && (
            <div>
              <div className="label-mono text-muted-foreground mb-2">Linked</div>
              <ul className="text-sm space-y-1">
                {roomName && (
                  <li className="inline-flex items-center gap-2 text-foreground">
                    <FolderLock className="w-3.5 h-3.5 text-primary" /> {roomName}
                  </li>
                )}
                {docName && (
                  <li className="inline-flex items-center gap-2 text-foreground">
                    <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" /> {docName}
                  </li>
                )}
              </ul>
            </div>
          )}

          {(task.assignees || []).length > 0 && (
            <div>
              <div className="label-mono text-muted-foreground mb-2">Assignees</div>
              <ul className="text-sm space-y-1">
                {task.assignees.map((aid) => {
                  const u = userById.get(aid);
                  return (
                    <li key={aid} className="inline-flex items-center gap-2 text-foreground mr-3">
                      <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-semibold tracking-wide ${avatarTone(u || { id: aid })}`}>
                        {u ? initials(u) : "??"}
                      </span>
                      {u ? (u.name || u.email) : aid}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {/* Comments */}
          <div>
            <div className="label-mono text-muted-foreground mb-2 inline-flex items-center gap-1">
              <MessageSquare className="w-3 h-3" /> Comments ({(task.comments || []).length})
            </div>
            <ul className="space-y-3">
              {(task.comments || []).map((c) => (
                <li key={c.id} className="border border-border rounded-md p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-medium text-foreground">{c.user_name || "User"}</span>
                    <span className="label-mono text-muted-foreground">{new Date(c.created_at).toLocaleString()}</span>
                  </div>
                  <p className="mt-1.5 text-sm text-foreground whitespace-pre-wrap">{c.body}</p>
                </li>
              ))}
              {(task.comments || []).length === 0 && (
                <li className="text-xs text-muted-foreground">No comments yet.</li>
              )}
            </ul>
            {canEdit && (
              <form onSubmit={submitComment} className="mt-3 flex items-end gap-2">
                <textarea
                  data-testid="task-comment-input"
                  rows={2}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder="Add a comment…"
                  className="flex-1 px-3 py-2 bg-background border border-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                />
                <button
                  type="submit"
                  disabled={busy || !comment.trim()}
                  data-testid="task-comment-submit"
                  className="px-3 py-2 rounded-md bg-primary text-primary-foreground text-sm hover:bg-primary/90 transition disabled:opacity-50"
                >
                  <Send className="w-3.5 h-3.5" />
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer actions */}
        <div className="border-t border-border p-4 flex items-center justify-between gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={onEdit}
              data-testid="task-detail-edit"
              className="px-3 py-2 rounded-md text-sm border border-border hover:bg-secondary transition"
            >
              Edit task
            </button>
          )}
          {canDelete && (
            <button
              type="button"
              onClick={onDelete}
              data-testid="task-detail-delete"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md text-sm border border-border text-rose-500 hover:bg-destructive hover:text-destructive-foreground hover:border-destructive transition"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          )}
        </div>
      </aside>
    </div>
  );
}
